import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Helmet } from 'react-helmet-async';
import GoogleLogin from './components/GoogleLogin';
import About from './components/About';
import Features from './components/Features';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import CookieConsent from './components/CookieConsent';
import CookieSettings from './components/CookieSettings';
import Contact from './components/Contact';
import { initializeGA, disableGA } from './utils/analytics';
import './App.css';

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin 
  : 'http://localhost:8000';
const APK_DOWNLOAD_URL = 'https://files.tmper.app/r2-upload/r2_upload.apk';
const SITE_ORIGIN = 'https://tmper.app';

// Thumbnail component with loading state
const ThumbnailImage = ({ src, alt, className = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [key, setKey] = useState(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    setKey(prev => prev + 1);
  }, [src]);

  const handleLoad = () => {
    setLoading(false);
    setError(false);
  };

  const handleError = () => {
    setLoading(false);
    setError(true);
  };

  return (
    <div className="thumbnail-container">
      {loading && (
        <div className="thumbnail-loading">
          <div className="thumbnail-spinner">
            <svg className="spinner-svg" width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="#f3f3f3"
                strokeWidth="2"
                fill="none"
              />
              <circle
                cx="10"
                cy="10"
                r="8"
                stroke="#6200EE"
                strokeWidth="2"
                fill="none"
                strokeDasharray="25 50"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      )}
      {error && (
        <div className="thumbnail-error">
          <Icon name="warning" size={20} />
        </div>
      )}
      <img
        key={key}
        src={src}
        alt={alt}
        className={`thumbnail ${className} ${loading ? 'loading' : ''}`}
        onLoad={handleLoad}
        onError={handleError}
        style={{ display: error ? 'none' : 'block' }}
      />
    </div>
  );
};

// Icon component
const Icon = ({ name, size = 24, className = '' }) => {
  const iconMap = {
    'camera-alt': '📷',
    'photo-library': '🖼️',
    'file-upload': '📁',
    'download': '⬇️',
    'settings': '⚙️',
    'content-copy': '📋',
    'delete': '🗑️',
    'close': '✕',
    'check-circle': '✅',
    'warning': '⚠️',
    'refresh': '🔄',
    'paste': '📋',
    'rocket': '🚀',
    'google': '🔑',
  };
  
  return (
    <span className={`icon ${className}`} style={{ fontSize: size }}>
      {iconMap[name] || '?'}
    </span>
  );
};

// Upload image data structure
class UploadedImage {
  constructor({ r2Url, filename, uploadTime }) {
    this.r2Url = r2Url;
    this.filename = filename;
    this.uploadTime = uploadTime ? new Date(uploadTime) : new Date();
  }

  static fromJson(json) {
    return new UploadedImage({
      r2Url: json.r2Url || json.url || '',
      filename: json.filename || '',
      uploadTime: json.uploadTime || new Date().toISOString(),
    });
  }

  toJson() {
    return {
      r2Url: this.r2Url,
      filename: this.filename,
      uploadTime: this.uploadTime.toISOString(),
    };
  }
}

// Upload file data structure (for non-image files)
class UploadedFile {
  constructor({ r2Url, filename, uploadTime, fileSize, contentType }) {
    this.r2Url = r2Url;
    this.filename = filename;
    this.uploadTime = uploadTime ? new Date(uploadTime) : new Date();
    this.fileSize = fileSize || 0;
    this.contentType = contentType || '';
  }

  static fromJson(json) {
    return new UploadedFile({
      r2Url: json.r2Url || json.url || '',
      filename: json.filename || '',
      uploadTime: json.uploadTime || new Date().toISOString(),
      fileSize: json.fileSize || json.size || 0,
      contentType: json.contentType || json.content_type || '',
    });
  }

  toJson() {
    return {
      r2Url: this.r2Url,
      filename: this.filename,
      uploadTime: this.uploadTime.toISOString(),
      fileSize: this.fileSize,
      contentType: this.contentType,
    };
  }
}

// Get file type icon based on filename or content type
const getFileTypeIcon = (filename, contentType = '') => {
  const ext = filename.split('.').pop().toLowerCase();
  const type = contentType.toLowerCase();

  // Check by extension first
  if (['pdf'].includes(ext)) return '📄';
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['txt', 'md', 'log'].includes(ext)) return '📃';
  if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(ext)) return '📦';
  if (['mp3', 'wav', 'flac', 'aac', 'm4a'].includes(ext)) return '🎵';
  if (['mp4', 'avi', 'mov', 'mkv', 'webm'].includes(ext)) return '🎬';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return '📊';
  if (['ppt', 'pptx'].includes(ext)) return '📑';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return '🖼️';

  // Check by content type
  if (type.includes('pdf')) return '📄';
  if (type.includes('word') || type.includes('document')) return '📝';
  if (type.includes('text')) return '📃';
  if (type.includes('zip') || type.includes('compressed')) return '📦';
  if (type.includes('audio')) return '🎵';
  if (type.includes('video')) return '🎬';
  if (type.includes('spreadsheet') || type.includes('excel')) return '📊';
  if (type.includes('presentation') || type.includes('powerpoint')) return '📑';
  if (type.includes('image')) return '🖼️';

  return '📎'; // Default icon
};

// Format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Generate or get browser ID for anonymous user tracking
const getBrowserId = () => {
  let browserId = localStorage.getItem('browser_id');
  if (!browserId) {
    // Generate a unique browser ID using timestamp and random string
    browserId = `browser_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('browser_id', browserId);
    console.log('Generated new browser ID:', browserId);
  }
  return browserId;
};

// Main App component for the home page
function HomePage() {
  const location = useLocation();
  const pathname = location?.pathname || '/';

  const seo = (() => {
    switch (pathname) {
      case '/temporary-image-upload':
        return {
          canonical: `${SITE_ORIGIN}/temporary-image-upload`,
          title: 'Temporary Image Upload | Share Images That Auto-Delete in 1 Hour | Tmper',
          description:
            'Temporary image upload and sharing. Upload a screenshot or photo, get a shareable link, and it auto-deletes after 1 hour. Free and fast, no signup required.',
          keywords:
            'temporary image upload, upload image temporarily, share image temporarily, temporary image hosting, screenshot upload, expiring image link',
          heroTitle: 'Temporary Image Upload',
          heroSubtitle:
            'Upload an image, copy the link, share it anywhere. Your link expires automatically after 1 hour.',
          faq: [
            { q: 'How long do uploads last?', a: 'Uploads automatically delete after 1 hour.' },
            { q: 'Do I need an account?', a: 'No. You can upload without signing in.' },
            { q: 'Can I upload screenshots?', a: 'Yes. Paste screenshots (Ctrl+V / Cmd+V), or drag and drop files.' },
          ],
        };
      case '/temporary-file-upload':
        return {
          canonical: `${SITE_ORIGIN}/temporary-file-upload`,
          title: 'Temporary File Upload | Share Files That Auto-Delete in 1 Hour | Tmper',
          description:
            'Temporary file upload and sharing. Upload any file up to 100MB, get a shareable link, and it auto-deletes after 1 hour. Free and fast.',
          keywords:
            'temporary file upload, temporary file hosting, upload file temporarily, expiring file link, temporary file sharing, 100MB upload',
          heroTitle: 'Temporary File Upload',
          heroSubtitle:
            'Upload a file, copy the link, share it anywhere. Your link expires automatically after 1 hour.',
          faq: [
            { q: 'What file types are supported?', a: 'Any file type. Images, PDFs, documents, archives, and more.' },
            { q: 'What is the max file size?', a: 'Up to 100MB per file.' },
            { q: 'How long do files stay available?', a: 'Files automatically delete after 1 hour.' },
          ],
        };
      default:
        return {
          canonical: `${SITE_ORIGIN}/`,
          title: 'Tmper - Free Temporary File & Image Hosting | Auto-Delete After 1 Hour',
          description:
            'Upload images and files instantly, get shareable links that auto-delete after 1 hour. Support any file up to 100MB. Free temporary hosting with drag-and-drop. No signup required.',
          keywords:
            'temporary file upload, temporary image upload, file hosting, image hosting, temp link, file sharing, free file hosting, temporary file storage, auto-delete files, clipboard upload, drag and drop upload, 100MB file upload, expiring upload link',
          heroTitle: 'Temporary File & Image Sharing',
          heroSubtitle:
            'Upload images and files instantly and get shareable links that automatically expire in 1 hour.',
          faq: [
            { q: 'How long do uploads last?', a: 'Uploads automatically delete after 1 hour.' },
            { q: 'What is the max file size?', a: 'Up to 100MB per file.' },
            { q: 'Can I paste from clipboard?', a: 'Yes. Paste images with Ctrl+V (Cmd+V on Mac).' },
          ],
        };
    }
  })();

  const jsonLdFaq = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (seo.faq || []).map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: a,
      },
    })),
  };

  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    percentage: 0,
    loaded: 0,
    total: 0,
    speed: 0,
    eta: 0
  });
  const [refreshing, setRefreshing] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [user, setUser] = useState(null);
  const [showGoogleLogin, setShowGoogleLogin] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [browserId] = useState(getBrowserId()); // Get browser ID once on component mount
  const fileInputRef = useRef(null);
  const generalFileInputRef = useRef(null);

  // R2 configuration
  const [config, setConfig] = useState({
    accountId: '',
    accessKeyId: '',
    secretAccessKey: '',
    bucketName: '',
    publicUrl: '',
  });

  useEffect(() => {
    loadR2Config();
    loadUserFromStorage();
    // Load upload history after user is loaded
    loadUploadHistory();
    loadFileHistory();
  }, []);

  // Handle paste events for image upload
  useEffect(() => {
    const handlePaste = (e) => {
      // Check if we're currently uploading
      if (isUploading) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      let foundImage = false;
      let hasNonImageContent = false;

      // Look for image items in the clipboard
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        // Track if we found non-image content
        if (item.kind === 'file' && item.type && item.type.indexOf('image') === -1) {
          hasNonImageContent = true;
        }

        if (item.type.indexOf('image') !== -1) {
          foundImage = true;
          const file = item.getAsFile();
          if (file) {
            // Check file size (limit to 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (file.size > maxSize) {
              showNotification(`Pasted image too large. Maximum size is 10MB. Pasted image is ${(file.size / 1024 / 1024).toFixed(1)}MB`, 'error');
              return;
            }

            // Give the file a proper name with timestamp
            const timestamp = new Date().getTime();
            const extension = item.type.split('/')[1] || 'png';
            const fileName = `paste-${timestamp}.${extension}`;

            // Create a new File object with a proper name
            const namedFile = new File([file], fileName, { type: item.type });

            console.log('Image pasted from clipboard:', fileName, `(${(file.size / 1024 / 1024).toFixed(2)}MB)`);
            showNotification('Processing pasted image...');
            uploadToR2(namedFile);
          }
          e.preventDefault();
          break;
        }
      }

      // If we found non-image content but no images, show helpful message
      if (!foundImage && hasNonImageContent) {
        showNotification('Paste only works for images. For other files, please use drag-and-drop or the "Choose File to Upload" button.', 'warning', 6000);
        e.preventDefault();
      }
    };
    
    // Add event listener
    document.addEventListener('paste', handlePaste);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('paste', handlePaste);
    };
  }, [isUploading, uploadedImages, user, browserId]); // Add necessary dependencies

  const showNotification = (message, type = 'success', duration = 3000) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, duration);
  };

  const loadUserFromStorage = () => {
    try {
      const savedUser = localStorage.getItem('google_user');
      if (savedUser) {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        console.log('User loaded:', userData.name);
      }
    } catch (error) {
      console.log('Failed to load user data:', error);
    }
  };

  const handleGoogleLoginSuccess = (userData) => {
    console.log('Google login success:', userData);
    setUser(userData);
    localStorage.setItem('google_user', JSON.stringify(userData));
    setShowGoogleLogin(false);
    showNotification(`Welcome, ${userData.name}!`);
    // Reload upload history with user filter
    syncWithBackend().catch(error => {
      console.log('Failed to sync after login:', error);
    });
  };

  const handleGoogleLoginError = (error) => {
    console.error('Google login error:', error);
    showNotification('Google login failed: ' + error, 'error');
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('google_user');
    showNotification('Logged out successfully');
    // Clear uploads and reload (will show all uploads for non-logged in users)
    setUploadedImages([]);
    syncWithBackend().catch(error => {
      console.log('Failed to sync after logout:', error);
      loadLocalHistory();
    });
  };

  const loadR2Config = () => {
    try {
      const savedConfig = localStorage.getItem('r2_config');
      if (savedConfig) {
        const configData = JSON.parse(savedConfig);
        setConfig(configData);
        setIsConfigured(true);
        console.log('R2 configuration loaded successfully');
      } else {
        console.log('No R2 configuration found, setting defaults...');
        setDefaultConfig();
      }
    } catch (error) {
      console.log('Failed to load R2 configuration:', error);
      setDefaultConfig();
    }
  };

  const setDefaultConfig = () => {
    const defaultConfig = {
      accountId: '76f1f4320ee5d80a191c881bcfdb0773',
      accessKeyId: '3656bdd9496ec3caa11c959d518f623',
      secretAccessKey: '811b8c4dfa1830b9e872b704ba38d172c24ed1988bf31fc0f5cb8835aa4aace',
      bucketName: 'tempurl',
      publicUrl: 'https://file.tmper.app',
    };

    saveR2Config(defaultConfig);
  };

  const saveR2Config = (newConfig) => {
    try {
      localStorage.setItem('r2_config', JSON.stringify(newConfig));
      setConfig(newConfig);
      setIsConfigured(true);
      console.log('R2 configuration saved successfully');
    } catch (error) {
      console.log('Failed to save R2 configuration:', error);
    }
  };

  const loadUploadHistory = async () => {
    try {
      // First try to sync from backend
      await syncWithBackend();
    } catch (error) {
      console.log('Failed to sync from backend, loading local data:', error);
      loadLocalHistory();
    }
  };

  // Helper function to filter out expired images (older than 1 hour)
  const filterExpiredImages = (images) => {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour in milliseconds
    
    return images.filter(image => {
      return image.uploadTime >= oneHourAgo;
    });
  };

  const syncWithBackend = async () => {
    console.log('Syncing upload history with backend...');
    try {
      // Get user email from state or localStorage
      let userEmail = null;
      if (user && user.email) {
        userEmail = user.email;
      } else {
        // Try to get from localStorage if user state not loaded yet
        const savedUser = localStorage.getItem('google_user');
        if (savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            userEmail = userData.email;
          } catch (e) {
            console.log('Could not parse saved user data');
          }
        }
      }

      // Build URL with appropriate filter parameter
      let url = `${API_BASE_URL}/uploads/`;
      const params = new URLSearchParams();
      
      if (userEmail) {
        params.append('user_email', userEmail);
        console.log(`Fetching uploads for user: ${userEmail}`);
      } else {
        // Use browser ID for anonymous users
        params.append('browser_id', browserId);
        console.log(`Fetching uploads for browser: ${browserId}`);
      }
      
      url += `?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        const uploads = responseData.uploads || [];

        const allImages = uploads.map(upload => UploadedImage.fromJson(upload));
        const validImages = filterExpiredImages(allImages);
        
        setUploadedImages(validImages);

        // Save synced data locally (only valid images)
        saveUploadHistory(validImages);
        
        if (validImages.length !== allImages.length) {
          console.log(`Filtered out ${allImages.length - validImages.length} expired images from backend data`);
        }
        console.log(`Successfully synced ${uploads.length} uploads from backend`);
      } else {
        throw new Error(`Backend returned ${response.status}`);
      }
    } catch (error) {
      console.log('Backend sync failed:', error);
      throw error;
    }
  };

  const loadLocalHistory = () => {
    try {
      const jsonString = localStorage.getItem('uploaded_images');
      if (jsonString) {
        const jsonList = JSON.parse(jsonString);
        const allImages = jsonList.map(json => UploadedImage.fromJson(json));
        const validImages = filterExpiredImages(allImages);
        
        // If we filtered out any expired images, update localStorage
        if (validImages.length !== allImages.length) {
          saveUploadHistory(validImages);
          console.log(`Filtered out ${allImages.length - validImages.length} expired images from local storage`);
        }
        
        setUploadedImages(validImages);
        console.log(`Loaded ${validImages.length} valid images from local storage (${allImages.length} total)`);
      }
    } catch (error) {
      console.log('Failed to load local history:', error);
    }
  };

  const saveUploadHistory = (images = uploadedImages) => {
    try {
      const jsonList = images.map(img => img.toJson());
      localStorage.setItem('uploaded_images', JSON.stringify(jsonList));
    } catch (error) {
      console.log('Failed to save upload history:', error);
    }
  };

  const handleFileSelect = (files, useCamera = false) => {
    if (files && files[0]) {
      const file = files[0];
      
      // Check file size (limit to 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        showNotification(`File too large. Maximum size is 10MB. Selected file is ${(file.size / 1024 / 1024).toFixed(1)}MB`, 'error');
        return;
      }
      
      console.log('File selected:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      uploadToR2(file);
    }
  };

  const openFileSelector = (useCamera = false) => {
    if (useCamera && cameraInputRef.current) {
      cameraInputRef.current.click();
    } else if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handlePasteButtonClick = async () => {
    try {
      // Try to read from clipboard
      const clipboardItems = await navigator.clipboard.read();
      
      for (const clipboardItem of clipboardItems) {
        // Check if clipboard has image type
        for (const type of clipboardItem.types) {
          if (type.startsWith('image/')) {
            const blob = await clipboardItem.getType(type);
            
            // Check file size (limit to 10MB)
            const maxSize = 10 * 1024 * 1024; // 10MB in bytes
            if (blob.size > maxSize) {
              showNotification(`Pasted image too large. Maximum size is 10MB. Pasted image is ${(blob.size / 1024 / 1024).toFixed(1)}MB`, 'error');
              return;
            }
            
            // Create a file from the blob
            const timestamp = new Date().getTime();
            const extension = type.split('/')[1] || 'png';
            const fileName = `paste-${timestamp}.${extension}`;
            const file = new File([blob], fileName, { type });
            
            console.log('Image pasted via button:', fileName, `(${(blob.size / 1024 / 1024).toFixed(2)}MB)`);
            showNotification('Processing pasted image...');
            uploadToR2(file);
            return;
          }
        }
      }
      
      showNotification('No image found in clipboard', 'warning');
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      // Fallback message for browsers that don't support clipboard API
      showNotification('Please use Ctrl+V/Cmd+V to paste images', 'warning');
    }
  };

  // Yield one frame before heavy/async work so UI can render spinner
  const waitNextFrame = () => new Promise(requestAnimationFrame);

  const uploadToR2 = async (file) => {
    console.log('Starting upload via Django backend...');
    setIsUploading(true);
    setUploadProgress({ percentage: 0, loaded: 0, total: file.size, speed: 0, eta: 0 });
    // Ensure the uploading indicator has a chance to render before we start
    // the upload/network work (prevents a frozen spinner on some browsers)
    try { await waitNextFrame(); } catch (_) {}

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      if (user && user.email) {
        formData.append('user_email', user.email);
        formData.append('user_name', user.name || '');
      } else {
        // Include browser ID for anonymous users
        formData.append('browser_id', browserId);
      }

      const xhr = new XMLHttpRequest();
      let startTime = Date.now();
      let lastLoaded = 0;
      let lastTime = startTime;

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded / e.total) * 100);
          const currentTime = Date.now();
          const timeDiff = (currentTime - lastTime) / 1000; // in seconds
          const loadedDiff = e.loaded - lastLoaded;

          // Calculate speed (bytes per second)
          const speed = timeDiff > 0 ? loadedDiff / timeDiff : 0;

          // Calculate ETA (estimated time remaining in seconds)
          const remaining = e.total - e.loaded;
          const eta = speed > 0 ? remaining / speed : 0;

          setUploadProgress({
            percentage,
            loaded: e.loaded,
            total: e.total,
            speed,
            eta
          });

          lastLoaded = e.loaded;
          lastTime = currentTime;
        }
      });

      xhr.addEventListener('load', () => {
        try {
          const responseData = JSON.parse(xhr.responseText);
          console.log('Response status:', xhr.status);
          console.log('Response content:', responseData);

          if (xhr.status === 200 && responseData.success) {
            const newImage = new UploadedImage({
              r2Url: responseData.url,
              filename: responseData.filename,
              uploadTime: new Date().toISOString(),
            });

            setUploadedImages(prevImages => {
              const updatedImages = [newImage, ...prevImages];
              saveUploadHistory(updatedImages);
              return updatedImages;
            });

            showNotification('Upload successful!');
            console.log('File upload successful!');
            console.log('R2 URL:', responseData.url);
            resolve(responseData);
          } else {
            const error = responseData.error || 'Unknown error';
            console.log('Django backend reported error:', error);
            showNotification(`Upload error: ${error}`, 'error');
            reject(new Error(error));
          }
        } catch (error) {
          console.log('Error parsing response:', error);
          showNotification(`Upload error: ${error.message}`, 'error');
          reject(error);
        } finally {
          setIsUploading(false);
          setUploadProgress({ percentage: 0, loaded: 0, total: 0, speed: 0, eta: 0 });
        }
      });

      xhr.addEventListener('error', () => {
        console.log('Django upload error: Network error');
        showNotification('Unable to connect to upload service, please check if the server is running', 'error');
        setIsUploading(false);
        setUploadProgress({ percentage: 0, loaded: 0, total: 0, speed: 0, eta: 0 });
        reject(new Error('Network error'));
      });

      xhr.addEventListener('abort', () => {
        console.log('Upload cancelled');
        setIsUploading(false);
        setUploadProgress({ percentage: 0, loaded: 0, total: 0, speed: 0, eta: 0 });
        reject(new Error('Upload cancelled'));
      });

      console.log('Sending request to Django backend...');
      xhr.open('POST', `${API_BASE_URL}/upload/`);
      xhr.send(formData);
    });
  };

  const handleGeneralFileSelect = (files) => {
    if (files && files[0]) {
      const file = files[0];

      // Check file size (limit to 100MB)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (file.size > maxSize) {
        showNotification(`File too large. Maximum size is 100MB. Selected file is ${(file.size / 1024 / 1024).toFixed(1)}MB`, 'error');
        return;
      }

      console.log('File selected:', file.name, `(${formatFileSize(file.size)})`);
      uploadFileToR2(file);
    }
  };

  const openGeneralFileSelector = () => {
    if (generalFileInputRef.current) {
      generalFileInputRef.current.click();
    }
  };

  const uploadFileToR2 = async (file) => {
    console.log('Starting file upload via Django backend...');
    setIsUploadingFile(true);
    setUploadProgress({ percentage: 0, loaded: 0, total: file.size, speed: 0, eta: 0 });
    try { await waitNextFrame(); } catch (_) {}

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      if (user && user.email) {
        formData.append('user_email', user.email);
        formData.append('user_name', user.name || '');
      } else {
        formData.append('browser_id', browserId);
      }

      const xhr = new XMLHttpRequest();
      let startTime = Date.now();
      let lastLoaded = 0;
      let lastTime = startTime;

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded / e.total) * 100);
          const currentTime = Date.now();
          const timeDiff = (currentTime - lastTime) / 1000; // in seconds
          const loadedDiff = e.loaded - lastLoaded;

          // Calculate speed (bytes per second)
          const speed = timeDiff > 0 ? loadedDiff / timeDiff : 0;

          // Calculate ETA (estimated time remaining in seconds)
          const remaining = e.total - e.loaded;
          const eta = speed > 0 ? remaining / speed : 0;

          setUploadProgress({
            percentage,
            loaded: e.loaded,
            total: e.total,
            speed,
            eta
          });

          lastLoaded = e.loaded;
          lastTime = currentTime;
        }
      });

      xhr.addEventListener('load', () => {
        try {
          const responseData = JSON.parse(xhr.responseText);
          console.log('Response status:', xhr.status);
          console.log('Response content:', responseData);

          if (xhr.status === 200 && responseData.success) {
            const newFile = new UploadedFile({
              r2Url: responseData.url,
              filename: responseData.filename,
              uploadTime: new Date().toISOString(),
              fileSize: responseData.size || file.size,
              contentType: responseData.content_type || file.type,
            });

            setUploadedFiles(prevFiles => {
              const updatedFiles = [newFile, ...prevFiles];
              saveFileHistory(updatedFiles);
              return updatedFiles;
            });

            showNotification('File upload successful!');
            console.log('File upload successful!');
            console.log('R2 URL:', responseData.url);
            resolve(responseData);
          } else {
            const error = responseData.error || 'Unknown error';
            console.log('Django backend reported error:', error);
            showNotification(`Upload error: ${error}`, 'error');
            reject(new Error(error));
          }
        } catch (error) {
          console.log('Error parsing response:', error);
          showNotification(`Upload error: ${error.message}`, 'error');
          reject(error);
        } finally {
          setIsUploadingFile(false);
          setUploadProgress({ percentage: 0, loaded: 0, total: 0, speed: 0, eta: 0 });
        }
      });

      xhr.addEventListener('error', () => {
        console.log('Django upload error: Network error');
        showNotification('Unable to connect to upload service, please check if the server is running', 'error');
        setIsUploadingFile(false);
        setUploadProgress({ percentage: 0, loaded: 0, total: 0, speed: 0, eta: 0 });
        reject(new Error('Network error'));
      });

      xhr.addEventListener('abort', () => {
        console.log('Upload cancelled');
        setIsUploadingFile(false);
        setUploadProgress({ percentage: 0, loaded: 0, total: 0, speed: 0, eta: 0 });
        reject(new Error('Upload cancelled'));
      });

      console.log('Sending file to Django backend...');
      xhr.open('POST', `${API_BASE_URL}/upload/`);
      xhr.send(formData);
    });
  };

  const loadFileHistory = () => {
    try {
      const jsonString = localStorage.getItem('uploaded_files');
      if (jsonString) {
        const jsonList = JSON.parse(jsonString);
        const allFiles = jsonList.map(json => UploadedFile.fromJson(json));
        const validFiles = filterExpiredImages(allFiles);

        if (validFiles.length !== allFiles.length) {
          saveFileHistory(validFiles);
          console.log(`Filtered out ${allFiles.length - validFiles.length} expired files from local storage`);
        }

        setUploadedFiles(validFiles);
        console.log(`Loaded ${validFiles.length} valid files from local storage (${allFiles.length} total)`);
      }
    } catch (error) {
      console.log('Failed to load file history:', error);
    }
  };

  const saveFileHistory = (files = uploadedFiles) => {
    try {
      const jsonList = files.map(file => file.toJson());
      localStorage.setItem('uploaded_files', JSON.stringify(jsonList));
    } catch (error) {
      console.log('Failed to save file history:', error);
    }
  };

  const deleteFile = (index) => {
    if (window.confirm('Are you sure you want to delete this file record?')) {
      const updatedFiles = [...uploadedFiles];
      updatedFiles.splice(index, 1);
      setUploadedFiles(updatedFiles);
      saveFileHistory(updatedFiles);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showNotification('Image link has been copied to clipboard');
    } catch (error) {
      console.log('Failed to copy to clipboard:', error);
      // Fallback: show URL for manual copying
      prompt('Copy failed, please manually copy the link:', url);
    }
  };

  const deleteImage = (index) => {
    if (window.confirm('Are you sure you want to delete this image record?')) {
      const updatedImages = [...uploadedImages];
      updatedImages.splice(index, 1);
      setUploadedImages(updatedImages);
      saveUploadHistory(updatedImages);
    }
  };

  const showImagePreview = (imageUrl, filename) => {
    setSelectedImage({ url: imageUrl, filename });
    setPreviewModalVisible(true);
  };


  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await syncWithBackend();
    } catch (error) {
      console.log('Refresh failed:', error);
    }
    setRefreshing(false);
  };



  const formatDate = (date) => {
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Function to truncate long URLs in the middle
  const truncateUrl = (url, maxLength = 45) => {
    if (!url || url.length <= maxLength) return url;
    
    // Keep the protocol and domain visible
    const urlParts = url.split('/');
    const domain = urlParts.slice(0, 3).join('/'); // https://file.tmper.app
    const filename = urlParts[urlParts.length - 1]; // image_xxx.jpg
    
    // If filename is too long, truncate it in the middle
    if (filename.length > 25) {
      const start = filename.substring(0, 12);
      const end = filename.substring(filename.length - 10);
      return `${domain}/...${end}`;
    }
    
    // Otherwise show domain and partial filename
    return `${domain}/.../${filename}`;
  };

  return (
    <div className="app">
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="keywords" content={seo.keywords} />

        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SITE_ORIGIN}/app_logo.png`} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/app_logo.png`} />

        <link rel="canonical" href={seo.canonical} />

        <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>
      </Helmet>
      
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />
      <input
        ref={generalFileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={(e) => handleGeneralFileSelect(e.target.files)}
      />

	      {/* Header */}
	      <header className="header">
	        <Link to="/" className="title-container" style={{ textDecoration: 'none' }}>
	          <img
	            src="/app_logo.png?v=2"
	            alt="Logo"
	            className="logo"
	          />
	          <div className="title">Tmper</div>
	        </Link>
	        <div className="header-actions">
	          <button 
	            className="header-button start-button"
	            onClick={() => {
	              const uploadSection = document.querySelector('.upload-section');
	              if (uploadSection) {
	                uploadSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
	              }
	            }}
	            title="Start using Tmper"
	            style={{ marginRight: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}
	          >
	            <Icon name="rocket" size={18} />
	            <span>Start</span>
	          </button>
	          {user ? (
	            <div className="user-info">
	              <span className="user-name">{user.name}</span>
	              <button 
	                className="header-button logout-button"
	                onClick={handleLogout}
	                title="Logout"
	              >
	                Logout
	              </button>
	            </div>
	          ) : (
	            <button 
	              className="header-button login-button"
	              onClick={() => setShowGoogleLogin(true)}
	              title="Login with Google"
	              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
	            >
	              <Icon name="google" size={18} />
	              <span>Login</span>
	            </button>
	          )}
	        </div>
	      </header>

      {/* Introduction section */}
      <section className="intro-section">
        <div className="intro-container">
          <h1 className="intro-title">{seo.heroTitle} - Auto-Delete After 1 Hour</h1>
          <p className="intro-subtitle">
            {seo.heroSubtitle} Perfect for quick, temporary sharing without cluttering your storage.
          </p>

          <div className="features-grid">
            <div className="feature-card">
              <span className="feature-icon">🔒</span>
              <h3>Temporary & Secure</h3>
              <p>Files auto-delete after 1 hour for privacy and security - share worry-free</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">📦</span>
              <h3>Any File Type</h3>
              <p>Upload images, PDFs, documents, archives, videos - any file up to 100MB</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">📱</span>
              <h3>Works Everywhere</h3>
              <p>Access from any device - mobile, tablet, or desktop</p>
            </div>
            <div className="feature-card">
              <span className="feature-icon">🤖</span>
              <h3>Perfect for Claude Code & Codex</h3>
              <p>Upload screenshots and files to get shareable links instantly for AI analysis</p>
            </div>
          </div>

          <div className="how-it-works">
            <h3>How to use:</h3>
            <ol className="steps-list">
              <li>Click "Choose File to Upload" for any file type (PDFs, documents, archives, etc.) or "Choose from Gallery" for images</li>
              <li>Or simply paste an image from your clipboard with Ctrl+V (Cmd+V on Mac)</li>
              <li>Wait for the upload to complete (usually under 5 seconds)</li>
              <li>Copy the generated link with one click</li>
              <li>Share your file link anywhere - remember it expires in 1 hour!</li>
            </ol>
            <div className="claude-code-section" style={{marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef'}}>
              <h4 style={{margin: '0 0 10px 0', color: '#495057'}}>💡 For Claude Code & Codex Users:</h4>
              <p style={{margin: '0', color: '#6c757d', fontSize: '14px'}}>
                Perfect for sharing screenshots and files with Claude Code or Codex! Upload your code screenshots or documents here, then paste the generated URL in your AI conversation and ask it to download and analyze the content.
              </p>
            </div>
            <p className="warning-text" style={{marginTop: '15px', color: '#ff6b6b', fontWeight: 'bold'}}>
              ⚠️ Important: All uploaded files are automatically deleted after 1 hour
            </p>
          </div>

          <div className="how-it-works" style={{ marginTop: '26px' }}>
            <h3>FAQ</h3>
            <ul className="steps-list" style={{ listStyle: 'disc', paddingLeft: '18px' }}>
              {(seo.faq || []).map((item, idx) => (
                <li key={idx}>
                  <strong>{item.q}</strong> {item.a}
                </li>
              ))}
            </ul>
            <p style={{ marginTop: '10px', color: '#6c757d', fontSize: '14px' }}>
              Looking for specific pages?{' '}
              <Link to="/temporary-image-upload">Temporary Image Upload</Link>
              {' · '}
              <Link to="/temporary-file-upload">Temporary File Upload</Link>
              {' · '}
              <Link to="/features">Features</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Upload buttons area */}
      <section className="upload-section">
        <div className="upload-container">
          <div className="button-row">
            <button
              className={`upload-button ${(isUploading || isUploadingFile) ? 'disabled' : ''}`}
              onClick={openGeneralFileSelector}
              disabled={isUploading || isUploadingFile}
            >
              <Icon name="file-upload" size={28} />
              <span>Choose File to Upload</span>
            </button>

            <button
              className={`upload-button ${(isUploading || isUploadingFile) ? 'disabled' : ''}`}
              onClick={() => openFileSelector(false)}
              disabled={isUploading || isUploadingFile}
            >
              <Icon name="photo-library" size={28} />
              <span>Choose from Gallery</span>
            </button>

            <button
              className={`upload-button paste-button ${(isUploading || isUploadingFile) ? 'disabled' : ''}`}
              onClick={handlePasteButtonClick}
              disabled={isUploading || isUploadingFile}
              title="Paste image from clipboard"
            >
              <Icon name="paste" size={28} />
              <span>Paste Image</span>
            </button>
          </div>

          <div className="drag-drop-area"
            onDrop={(e) => {
              e.preventDefault();
              const files = e.dataTransfer.files;
              if (files.length > 0) handleGeneralFileSelect(files);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDragEnter={(e) => e.preventDefault()}
          >
            <Icon name="photo-library" size={40} />
            <p>Or drag and drop any file here (max 100MB)</p>
            <p className="paste-hint">You can also paste images with Ctrl+V / Cmd+V</p>
          </div>
        </div>

        {(isUploading || isUploadingFile) && (
          <div className="uploading-indicator">
            <div className="spinner-container">
              <svg className="spinner-svg" width="20" height="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="#f3f3f3"
                  strokeWidth="2"
                  fill="none"
                />
                <circle
                  cx="10"
                  cy="10"
                  r="8"
                  stroke="#6200EE"
                  strokeWidth="2"
                  fill="none"
                  strokeDasharray="25 50"
                  strokeLinecap="round"
                />
              </svg>
            </div>
            <div className="upload-progress-info">
              <span className="upload-status">Uploading...</span>
              {uploadProgress.total > 0 && (
                <>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress.percentage}%` }}></div>
                  </div>
                  <div className="progress-details">
                    <span className="progress-percentage">{uploadProgress.percentage}%</span>
                    <span className="progress-size">{formatFileSize(uploadProgress.loaded)} / {formatFileSize(uploadProgress.total)}</span>
                    {uploadProgress.speed > 0 && (
                      <>
                        <span className="progress-speed">{formatFileSize(uploadProgress.speed)}/s</span>
                        {uploadProgress.eta > 0 && uploadProgress.eta < 3600 && (
                          <span className="progress-eta">ETA: {Math.ceil(uploadProgress.eta)}s</span>
                        )}
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </section>

      {/* Upload History - Images and Files */}
      <main className="list-container">
        <div className="list-header">
          <h2>Upload History</h2>
          <button 
            className={`refresh-button ${refreshing ? 'refreshing' : ''}`}
            onClick={onRefresh}
            disabled={refreshing}
          >
            <Icon name="refresh" size={20} />
          </button>
        </div>

        {(() => {
          // Merge and sort all uploads by time
          const allUploads = [
            ...uploadedImages.map(img => ({ ...img, type: 'image' })),
            ...uploadedFiles.map(file => ({ ...file, type: 'file' }))
          ].sort((a, b) => b.uploadTime - a.uploadTime);

          if (allUploads.length === 0) {
            return (
              <div className="empty-state">
                <Icon name="photo-library" size={48} />
                <h3>No files uploaded yet</h3>
                <p>
                  Start by clicking the upload buttons above or drag and drop a file
                </p>
              </div>
            );
          }

          return (
            <div className="images-grid">
              {allUploads.map((item, index) => {
                const isImage = item.type === 'image';
                const fileIcon = !isImage ? getFileTypeIcon(item.filename, item.contentType) : null;

                return (
                  <div key={`${item.type}-${index}`} className="image-card">
                    <button
                      className="delete-button"
                      onClick={() => {
                        if (isImage) {
                          const imgIndex = uploadedImages.findIndex(img => img.r2Url === item.r2Url);
                          if (imgIndex !== -1) deleteImage(imgIndex);
                        } else {
                          const fileIndex = uploadedFiles.findIndex(file => file.r2Url === item.r2Url);
                          if (fileIndex !== -1) deleteFile(fileIndex);
                        }
                      }}
                      title="Delete"
                    >
                      <Icon name="delete" size={20} />
                    </button>

                    {isImage ? (
                      <div
                        className="image-preview"
                        onClick={() => showImagePreview(item.r2Url, item.filename)}
                      >
                        <ThumbnailImage
                          src={item.r2Url}
                          alt={item.filename}
                        />
                      </div>
                    ) : (
                      <div className="file-icon-preview">
                        <span className="file-type-icon-large">{fileIcon}</span>
                        <span className="file-extension">{item.filename.split('.').pop().toUpperCase()}</span>
                      </div>
                    )}

                    <div className="image-info">
                      <p className="filename" title={item.filename}>
                        {item.filename}
                      </p>
                      <p className="upload-time">
                        {isImage ? formatDate(item.uploadTime) : `${formatFileSize(item.fileSize)} • ${formatDate(item.uploadTime)}`}
                      </p>

                      <div className="url-section">
                        <label className="url-label">{isImage ? 'Image' : 'File'} Link:</label>
                        <div className="url-row">
                          <span className="url-text r2-url" title={item.r2Url}>
                            {truncateUrl(item.r2Url)}
                          </span>
                          <button
                            className="copy-button"
                            onClick={() => copyToClipboard(item.r2Url)}
                            title="Copy Full Link"
                          >
                            <Icon name="content-copy" size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </main>


      {/* Image preview modal */}
      {previewModalVisible && selectedImage && (
        <ImagePreviewModal
          imageUrl={selectedImage.url}
          filename={selectedImage.filename}
          onClose={() => setPreviewModalVisible(false)}
        />
      )}

      {/* Google Login Modal */}
      {showGoogleLogin && (
        <div className="modal-overlay" onClick={() => setShowGoogleLogin(false)}>
          <div className="login-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={() => setShowGoogleLogin(false)}>
              <Icon name="close" size={24} />
            </button>
            <h2>Login with Google</h2>
            <p>Sign in to save your upload history across devices</p>
            <GoogleLogin 
              onSuccess={handleGoogleLoginSuccess}
              onError={handleGoogleLoginError}
            />
          </div>
        </div>
      )}

      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          <div className="notification-content">
            <span className="notification-message">{notification.message}</span>
            <button 
              className="notification-close" 
              onClick={() => setNotification({ show: false, message: '', type: 'success' })}
            >
              <Icon name="close" size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// Image preview modal component
const ImagePreviewModal = ({ imageUrl, filename, onClose }) => {
  if (!imageUrl) return null;

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <button className="preview-close-button" onClick={onClose}>
        <Icon name="close" size={28} />
      </button>
      
      <div className="preview-header">
        <h3 className="preview-title">{filename}</h3>
      </div>

      <div className="preview-image-container">
        <img
          src={imageUrl}
          alt={filename}
          className="preview-image"
        />
      </div>
    </div>
  );
};

// Footer component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="app-footer">
      <div className="footer-content">
        <div className="footer-links">
          <Link to="/temporary-image-upload" className="footer-link">Temporary Image Upload</Link>
          <span className="footer-separator">•</span>
          <Link to="/temporary-file-upload" className="footer-link">Temporary File Upload</Link>
          <span className="footer-separator">•</span>
          <Link to="/features" className="footer-link">Features</Link>
          <span className="footer-separator">•</span>
          <Link to="/about" className="footer-link">About</Link>
          <span className="footer-separator">•</span>
          <Link to="/privacy" className="footer-link">Privacy Policy</Link>
          <span className="footer-separator">•</span>
          <Link to="/terms" className="footer-link">Terms of Service</Link>
          <span className="footer-separator">•</span>
          <Link to="/cookies" className="footer-link">Cookie Settings</Link>
          <span className="footer-separator">•</span>
          <Link to="/contact" className="footer-link">Contact</Link>
        </div>
        <div className="footer-text">
          © {currentYear} Tmper. All rights reserved.
        </div>
        <div className="footer-badges" style={{ display: 'flex', gap: '15px', justifyContent: 'center', alignItems: 'center', margin: '15px 0', flexWrap: 'wrap' }}>
          <a
            href="https://www.producthunt.com/products/tmper?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-tmper"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1011164&theme=light&t=1763084118752"
              alt="Tmper - Paste screenshots, get temporary links instantly | Product Hunt"
              style={{ width: '250px', height: '54px' }}
              width="250"
              height="54"
            />
          </a>
          <a href="https://toolfame.com/item/tmper" target="_blank" rel="noopener noreferrer">
            <img
              src="https://toolfame.com/badge-light.svg"
              alt="Featured on toolfame.com"
              style={{ height: '54px', width: 'auto' }}
            />
          </a>
        </div>
      </div>
    </footer>
  );
};

// Main App component with routing
function App() {
  const [consentGiven, setConsentGiven] = useState(false);

  // Handle cookie consent changes
  const handleConsentChange = (preferences) => {
    if (preferences.analytics) {
      // User accepted analytics - initialize Google Analytics
      initializeGA();
      setConsentGiven(true);
    } else {
      // User rejected analytics - disable Google Analytics
      disableGA();
      setConsentGiven(false);
    }
  };

  // Check for existing consent on mount
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (consent) {
      try {
        const preferences = JSON.parse(consent);
        handleConsentChange(preferences);
      } catch (e) {
        console.error('Failed to load cookie preferences:', e);
      }
    }
  }, []);

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<><HomePage /><Footer /></>} />
        <Route path="/temporary-image-upload" element={<><HomePage /><Footer /></>} />
        <Route path="/temporary-file-upload" element={<><HomePage /><Footer /></>} />
        <Route path="/about" element={<><About /><Footer /></>} />
        <Route path="/features" element={<><Features /><Footer /></>} />
        <Route path="/privacy" element={<><PrivacyPolicy /><Footer /></>} />
        <Route path="/terms" element={<><TermsOfService /><Footer /></>} />
        <Route path="/cookies" element={<><CookieSettings /><Footer /></>} />
        <Route path="/contact" element={<><Contact /><Footer /></>} />
      </Routes>

      {/* Cookie Consent Banner */}
      <CookieConsent onConsentChange={handleConsentChange} />
    </div>
  );
}

export default App;
